import React, { useEffect, useState } from 'react';
import { Button, Spinner } from 'react-bootstrap';
import { AppNotification } from '../../types/responses/app-notification';
import {
  GetAppNotifications,
  MarkAllNotificationsAsReadAsync,
  MarkNotificationAsReadAsync
} from '../../services/notification';
import {
  getNotificationMessage,
  NotificationType
} from '../../types/notification-type-mapping';
import { store } from '../../stores/store';

const UserNotification: React.FC = () => {
  const [notifications, setNotifications] = useState<AppNotification[]>([]);
  const [loading, setLoading] = useState(true);
  const [clearing, setClearing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const response = await GetAppNotifications();
      setNotifications(response.data ?? []);
    } catch (err) {
      setError('Failed to load notifications');
      console.error('Error fetching notifications:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotifications();
  }, []);

  const handleClearAll = async () => {
    try {
      setClearing(true);
      await MarkAllNotificationsAsReadAsync();
      setNotifications([]);
      store.notificationStore.fetchUnreadCount();
    } catch (err) {
      setError('Failed to clear notifications');
      console.error('Error clearing notifications:', err);
    } finally {
      setClearing(false);
    }
  };

  const handleMarkAsRead = async (notificationId: number) => {
    try {
      await MarkNotificationAsReadAsync(notificationId);
      setNotifications((prev) => prev.filter((n) => n.id !== notificationId));
      store.notificationStore.fetchUnreadCount();
    } catch (err) {
      setError('Failed to mark notification as read');
      console.error('Error marking notification as read:', err);
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return `${hours}:${minutes} ${month}/${day}`;
  };

  const parseMetadata = (metadata?: string): string | null => {
    if (!metadata) return null;
    try {
      const parsed = JSON.parse(metadata);
      return parsed.entityName ?? null;
    } catch {
      return metadata;
    }
  };

  return (
    <div className={'flex-grow-1 pt-2 d-flex flex-column gap-2'}>
      <div className={'border-2 border-bottom'}>
        <div className={'text-success d-flex align-items-center gap-2 pb-2'}>
          <span className="material-symbols-outlined">notifications</span>
          <h2 className={'m-0'}>Notifications</h2>
        </div>
      </div>

      <div
        className={
          'd-flex justify-content-end mt-2 pb-2 border-2 border-bottom'
        }>
        <Button
          variant="secondary"
          onClick={handleClearAll}
          disabled={clearing || notifications.length === 0}>
          {clearing ? (
            <>
              <Spinner size="sm" animation="border" className="me-2" />
              Clearing...
            </>
          ) : (
            'Clear all'
          )}
        </Button>
      </div>

      <div className="mt-2 px-2 d-flex flex-column gap-3">
        {loading && (
          <div className="d-flex justify-content-center py-4">
            <Spinner animation="border" variant="primary" />
          </div>
        )}

        {error && (
          <div className="alert alert-danger" role="alert">
            {error}
          </div>
        )}

        {!loading && !error && notifications.length === 0 && (
          <div className="text-center text-muted py-4">
            No notifications found
          </div>
        )}

        {!loading &&
          !error &&
          notifications.map((notification) => (
            <div key={notification.id} className="p-3 row border bg-light">
              <div
                className="col-2 text-black-50"
                style={{
                  fontSize: '14px'
                }}>
                {formatDate(notification.createdDateUtc)}
              </div>
              <div className="col-9 pl-0">
                {notification.createdBy &&
                  notification.type !== NotificationType.EventCompleted && (
                    <span>{notification.createdBy} </span>
                  )}
                {/* For EventCompleted, show entity name first */}
                {notification.type === NotificationType.EventCompleted &&
                  parseMetadata(notification.metadata) && (
                    <b>{parseMetadata(notification.metadata)} </b>
                  )}
                {getNotificationMessage(
                  notification.type,
                  notification.metadata
                )}
                {/* For other types, show entity name after message */}
                {notification.type !== NotificationType.EventCompleted &&
                  parseMetadata(notification.metadata) && (
                    <span>
                      <b> {parseMetadata(notification.metadata)}</b>
                    </span>
                  )}
              </div>
              <div className="col-1 d-flex justify-content-end">
                <span
                  className="material-symbols-outlined"
                  style={{
                    height: 'fit-content',
                    cursor: 'pointer'
                  }}
                  onClick={() => handleMarkAsRead(notification.id)}>
                  close
                </span>
              </div>
            </div>
          ))}
      </div>
    </div>
  );
};

export default UserNotification;
